import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import apiService from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlineUserPlus,
  HiOutlineGlobeAmericas,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
} from '../../components/ui'

export default function CreateClientPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()
  const { canCreate } = usePermissionCheck()

  useEffect(() => {
    if (!canCreate('client')) {
      showError(t('common.permissionDenied'))
      navigate(-1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate])

  const [formData, setFormData] = useState({ name: '', email: '', mobile: '', country: '' })
  const [countries, setCountries] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [countriesLoading, setCountriesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const response = await apiService.get('/countries/')
        if (!cancelled && response.success) setCountries(response.data.data || [])
      } catch { /* swallow */ } finally {
        if (!cancelled) setCountriesLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const validate = () => {
    const next = {}
    if (!formData.name.trim()) next.name = t('clients.create.validationNameRequired', { entity: getEntity('client').singular })
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = t('clients.create.validationEmailInvalid')
    if (formData.mobile && !/^\d+$/.test(formData.mobile)) next.mobile = t('clients.create.validationMobileInvalid')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    try {
      setLoading(true)
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        mobile: formData.mobile.trim() || null,
        country: formData.country || null,
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.post('/clients/', payload, { params })
      if (response.success) {
        showSuccess(t('clients.create.successMessage', { entity: getEntity('client').singular }))
        navigate('/clients')
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
      }
    } catch {
      showError(t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('cases.detail.backTo', { entity: getEntity('client').plural })}
          variant="ghost"
          onClick={() => navigate('/clients')}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlineUserPlus}
          iconTone="success"
          title={t('clients.create.title', { entity: getEntity('client').singular.toLowerCase() })}
          subtitle={
            isWorkspaceSwitched
              ? t('clients.create.subtitleSwitched', { entity: getEntity('client').singular.toLowerCase(), name: user?.switchedAccountName })
              : t('clients.create.subtitle', { entity: getEntity('client').singular.toLowerCase() })
          }
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineUser className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('clients.create.sectionTitle', { entity: getEntity('client').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField
            label={t('clients.create.nameLabel', { entity: getEntity('client').singular })}
            required
            error={errors.name}
          >
            <Input
              name="name"
              size="lg"
              value={formData.name}
              onChange={handleChange}
              leadingIcon={HiOutlineUser}
              placeholder={t('clients.create.namePlaceholder', { entity: getEntity('client').singular.toLowerCase() })}
              disabled={loading}
              autoFocus
            />
          </FormField>

          <FormField
            label={t('clients.create.emailLabel')}
            error={errors.email}
            help={t('clients.create.emailHelp')}
          >
            <Input
              type="email"
              name="email"
              size="lg"
              value={formData.email}
              onChange={handleChange}
              leadingIcon={HiOutlineEnvelope}
              placeholder={t('clients.create.emailPlaceholder')}
              disabled={loading}
            />
          </FormField>

          <FormField
            label={t('clients.create.mobileLabel')}
            error={errors.mobile}
            help={t('clients.create.mobileHelp')}
          >
            <Input
              type="tel"
              name="mobile"
              size="lg"
              value={formData.mobile}
              onChange={handleChange}
              leadingIcon={HiOutlinePhone}
              placeholder={t('clients.create.mobilePlaceholder')}
              disabled={loading}
            />
          </FormField>

          <FormField
            label={t('clients.create.countryLabel')}
            error={errors.country}
            help={t('clients.create.countryHelp', { entity: getEntity('client').singular })}
          >
            <Select
              name="country"
              size="lg"
              value={formData.country}
              onChange={handleChange}
              disabled={loading || countriesLoading}
            >
              <option value="">{countriesLoading ? t('clients.create.countryLoading') : t('clients.create.countryPlaceholder')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Select>
          </FormField>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/clients')}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
            >
              {loading
                ? t('clients.create.submitLoading', { entity: getEntity('client').singular.toLowerCase() })
                : t('clients.create.submit', { entity: getEntity('client').singular.toLowerCase() })}
            </Button>
          </div>
        </form>
      </Card>

      <Banner tone="info" title={t('clients.create.guidelinesTitle', { entity: getEntity('client').singular })}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('clients.create.guideline1', { entity: getEntity('client').singular.toLowerCase() })}</li>
          <li>{t('clients.create.guideline2')}</li>
          <li>{t('clients.create.guideline3')}</li>
          <li>{t('clients.create.guideline4', { entity: getEntity('client').singular.toLowerCase() })}</li>
        </ul>
      </Banner>
    </div>
  )
}
